import type {
  GovernorRuntimeEvidenceEvent,
  GovernorRuntimeEvidenceSnapshot,
} from '../runtime-evidence/GovernorRuntimeEvidenceBus';
import {
  TREE_RUNTIME_SELECTOR_DIAGNOSTIC_KEYS,
  createEmptyTreeRuntimeSelectorDiagnostics,
  sanitizeTreeRuntimeSelectorDiagnostics,
  type TreeRuntimeSelectorDiagnostics,
} from './TreeRuntimeSelectorDiagnostics';
import {
  TREE_RUNTIME_SELECTOR_DISCREPANCY_KEYS,
  createEmptyTreeRuntimeSelectorDiscrepancyClassification,
  sanitizeTreeRuntimeSelectorDiscrepancyClassification,
  type TreeRuntimeSelectorDiscrepancyClassification,
} from './TreeRuntimeSelectorDiscrepancyClassification';
import {
  TREE_RUNTIME_SELECTOR_DISCREPANCY_SOURCE_KEYS,
  createEmptyTreeRuntimeSelectorDiscrepancySourceClassification,
  sanitizeTreeRuntimeSelectorDiscrepancySourceClassification,
  type TreeRuntimeSelectorDiscrepancySourceClassification,
} from './TreeRuntimeSelectorDiscrepancySourceClassification';

export type TreeRuntimeConvergenceLiveStatus =
  | 'NOT_OBSERVED'
  | 'READY'
  | 'DEGRADED'
  | 'UNSAFE';

export type TreeRuntimeConvergenceEnforcementReadiness =
  | 'BLOCKED_NO_LIVE_EVIDENCE'
  | 'REVIEW_ELIGIBLE'
  | 'BLOCKED_DEGRADED'
  | 'BLOCKED_UNSAFE';

export interface TreeRuntimeConvergenceLiveProjection {
  readonly status:
    TreeRuntimeConvergenceLiveStatus;
  readonly enforcementReadiness:
    TreeRuntimeConvergenceEnforcementReadiness;
  readonly observed: boolean;
  readonly observedAt: string | null;
  readonly convergenceStatus:
    'READY' | 'BLOCKED' | null;
  readonly containmentAction:
    | 'RENDER_CANONICAL'
    | 'RENDER_WITH_WARNING'
    | 'SUPPRESS_UNCERTIFIED_GEOMETRY'
    | null;
  readonly blockers: readonly string[];
  readonly blockerCount: number;
  readonly productionVisibleMemberCount:
    number | null;
  readonly missingProjectionCount:
    number | null;
  readonly missingGeometryRoleCount:
    number | null;
  readonly missingConnectorCount:
    number | null;

  readonly selectorDiagnostics:
    TreeRuntimeSelectorDiagnostics;

  readonly selectorDiscrepancyClassification:
    TreeRuntimeSelectorDiscrepancyClassification;

  readonly selectorDiscrepancySourceClassification:
    TreeRuntimeSelectorDiscrepancySourceClassification;

  readonly readOnly: true;
  readonly enforcementExecuted: false;
  readonly repairExecuted: false;
}

const EMPTY_PROJECTION:
  TreeRuntimeConvergenceLiveProjection =
  Object.freeze({
    status: 'NOT_OBSERVED',
    enforcementReadiness:
      'BLOCKED_NO_LIVE_EVIDENCE',
    observed: false,
    observedAt: null,
    convergenceStatus: null,
    containmentAction: null,
    blockers: Object.freeze([]),
    blockerCount: 0,
    productionVisibleMemberCount: null,
    missingProjectionCount: null,
    missingGeometryRoleCount: null,
    missingConnectorCount: null,
    selectorDiagnostics:
      createEmptyTreeRuntimeSelectorDiagnostics(),
    selectorDiscrepancyClassification:
      createEmptyTreeRuntimeSelectorDiscrepancyClassification(),
    selectorDiscrepancySourceClassification:
      createEmptyTreeRuntimeSelectorDiscrepancySourceClassification(),
    readOnly: true,
    enforcementExecuted: false,
    repairExecuted: false,
  });

const parseEvidence =
  (
    event:
      GovernorRuntimeEvidenceEvent,
  ): ReadonlyMap<string, string> => {
    const values =
      new Map<string, string>();

    for (const item of event.evidence) {
      const separator =
        item.indexOf('=');

      if (separator <= 0) {
        continue;
      }

      values.set(
        item.slice(0, separator),
        item.slice(separator + 1),
      );
    }

    return values;
  };

const numberOrNull =
  (
    value:
      string |
      undefined,
  ): number | null => {
    if (value === undefined) {
      return null;
    }

    const parsed =
      Number(value);

    return Number.isFinite(parsed)
      ? parsed
      : null;
  };

export const projectTreeRuntimeConvergenceEvidence =
  (
    snapshot:
      GovernorRuntimeEvidenceSnapshot,
  ): TreeRuntimeConvergenceLiveProjection => {
    const event =
      [...snapshot.events]
        .reverse()
        .find(
          candidate =>
            candidate.domain === 'tree' &&
            candidate.source ===
              'TreeRuntimeConvergenceEvidence',
        ) ??
      null;

    if (!event) {
      return EMPTY_PROJECTION;
    }

    const evidence =
      parseEvidence(event);

    const authenticated =
      evidence.get(
        'authenticatedSession',
      ) === 'true';

    if (!authenticated) {
      return EMPTY_PROJECTION;
    }

    const containment =
      evidence.get(
        'containmentStatus',
      );

    const status:
      TreeRuntimeConvergenceLiveStatus =
      containment === 'READY' ||
      containment === 'DEGRADED' ||
      containment === 'UNSAFE'
        ? containment
        : 'NOT_OBSERVED';

    if (status === 'NOT_OBSERVED') {
      return EMPTY_PROJECTION;
    }

    const action =
      evidence.get(
        'containmentAction',
      );

    const containmentAction =
      action === 'RENDER_CANONICAL' ||
      action === 'RENDER_WITH_WARNING' ||
      action ===
        'SUPPRESS_UNCERTIFIED_GEOMETRY'
        ? action
        : null;

    const convergence =
      evidence.get(
        'convergenceStatus',
      );

    const convergenceStatus =
      convergence === 'READY' ||
      convergence === 'BLOCKED'
        ? convergence
        : null;

    const blockersValue =
      evidence.get('blockers') ??
      'none';

    const blockers =
      blockersValue === 'none'
        ? Object.freeze([])
        : Object.freeze(
            blockersValue
              .split(',')
              .map(value => value.trim())
              .filter(Boolean),
          );

    const enforcementReadiness:
      TreeRuntimeConvergenceEnforcementReadiness =
      status === 'READY'
        ? 'REVIEW_ELIGIBLE'
        : status === 'DEGRADED'
          ? 'BLOCKED_DEGRADED'
          : 'BLOCKED_UNSAFE';

    const selectorDiagnosticsCandidate =
      Object.fromEntries(
        TREE_RUNTIME_SELECTOR_DIAGNOSTIC_KEYS.map(
          key => [
            key,
            numberOrNull(
              evidence.get(
                `selector.${key}`,
              ),
            ),
          ],
        ),
      );

    const selectorDiagnostics =
      sanitizeTreeRuntimeSelectorDiagnostics(
        selectorDiagnosticsCandidate,
      );

    if (!selectorDiagnostics) {
      return EMPTY_PROJECTION;
    }

    const selectorDiscrepancyCandidate =
      Object.fromEntries(
        TREE_RUNTIME_SELECTOR_DISCREPANCY_KEYS.map(
          key => [
            key,
            numberOrNull(
              evidence.get(
                `discrepancy.${key}`,
              ),
            ),
          ],
        ),
      );

    const selectorDiscrepancyClassification =
      sanitizeTreeRuntimeSelectorDiscrepancyClassification(
        selectorDiscrepancyCandidate,
      );

    if (!selectorDiscrepancyClassification) {
      return EMPTY_PROJECTION;
    }

    const selectorDiscrepancySourceCandidate =
      Object.fromEntries(
        TREE_RUNTIME_SELECTOR_DISCREPANCY_SOURCE_KEYS.map(
          key => [
            key,
            numberOrNull(
              evidence.get(
                `sourceDiscrepancy.${key}`,
              ),
            ),
          ],
        ),
      );

    const selectorDiscrepancySourceClassification =
      sanitizeTreeRuntimeSelectorDiscrepancySourceClassification(
        selectorDiscrepancySourceCandidate,
      );

    if (!selectorDiscrepancySourceClassification) {
      return EMPTY_PROJECTION;
    }

    return Object.freeze({
      status,
      enforcementReadiness,
      observed: true,
      observedAt:
        evidence.get('observedAt') ??
        event.timestamp,
      convergenceStatus,
      containmentAction,
      blockers,
      blockerCount:
        numberOrNull(
          evidence.get('blockerCount'),
        ) ??
        blockers.length,
      productionVisibleMemberCount:
        numberOrNull(
          evidence.get(
            'productionVisibleMemberCount',
          ),
        ),
      missingProjectionCount:
        numberOrNull(
          evidence.get(
            'missingProjectionCount',
          ),
        ),
      missingGeometryRoleCount:
        numberOrNull(
          evidence.get(
            'missingGeometryRoleCount',
          ),
        ),
      missingConnectorCount:
        numberOrNull(
          evidence.get(
            'missingConnectorCount',
          ),
        ),
      selectorDiagnostics,
      selectorDiscrepancyClassification,
      selectorDiscrepancySourceClassification,
      readOnly: true,
      enforcementExecuted: false,
      repairExecuted: false,
    });
  };
